import { createFileRoute, useParams, Link } from '@tanstack/react-router'
import { ArrowLeft, Users, Calendar, ClipboardCheck, Shield, Globe, CreditCard } from 'lucide-react'

export const Route = createFileRoute('/feature/admin/$id')({
  component: AdminFeatureDetailPage,
})

interface Feature {
  id: number
  icon: JSX.Element
  title: string
  summary: string
  keyPoints: string[]
  detailedDescription: string
}

// Feature data - in production, this would come from an API
const features: Feature[] = [
  {
    id: 1,
    icon: <Users className="w-8 h-8" />,
    title: 'School Management System',
    summary: "The School Management System is the backbone of JeridSchool's Admin panel. It provides the Admin with complete control over every academic and operational aspect of the school. From one central dashboard, the Admin can manage students, teachers, parents, grades, classes, subjects, and rooms — all in one organized digital ecosystem.",
    keyPoints: [
      'Complete control over students, teachers, parents, grades, classes, subjects, and rooms from one central dashboard',
      'Track student subscription payments and manage bulk imports or individual student profiles',
      'Multilingual support for Arabic, French, and English with total digital control eliminating paper files and manual scheduling'
    ],
    detailedDescription: `The Admin begins by setting up the school's structure, including grade levels (for example, primary, secondary, or university), and defining the class groups within each grade. Each class can then be assigned its own homeroom teacher, subjects, and student list.

Next, the Admin manages subjects — creating or importing them, defining their weekly hours, and linking each subject to a qualified teacher. This ensures that every class has a clear academic schedule with no overlap or confusion.

The system also allows the creation and organization of rooms, including classrooms, laboratories, computer labs, and even virtual rooms for online sessions. Each room can be assigned a capacity, availability schedule, and purpose, helping avoid conflicts or double-booking.

Parent management is another key part of the Admin's role. Parents are added and linked directly to their child's profile, allowing them to monitor attendance, assignments, and progress in real time. The platform automatically synchronizes parent-child relationships and sends notifications when updates occur.

Students can be imported in bulk or added individually, with full profiles including personal data, academic level, and class assignments. If a student changes grade or transfers, the Admin can easily update their record without losing any history. You can also track if this student has paid their subscription or still has not.

The Admin also oversees teachers, assigning them to subjects and classes, defining schedules, and tracking their teaching load. This helps maintain a balanced and transparent system where everyone's responsibilities are clear.

All data is interconnected — changing a subject, teacher, or room automatically updates related records, ensuring accuracy and consistency across the system. The interface is designed for ease of use, with multilingual support for Arabic, French, and English, making it ideal for Tunisian institutions.

The main benefit is total digital control — no more paper files, Excel sheets, or manual scheduling. The Admin gains a real-time view of the entire school, improving efficiency, accuracy, and communication. JeridSchool's School Management System transforms complex administrative tasks into a smooth, visual, and intelligent management experience, empowering schools to operate more efficiently and focus on education rather than paperwork.`
  },
  {
    id: 2,
    icon: <Calendar className="w-8 h-8" />,
    title: 'Timetable Management System',
    summary: "The Timetable Management System in JeridSchool allows the Admin to design, organize, and control the entire school schedule with complete flexibility and accuracy. It's the tool that ensures every class, subject, and teacher fits perfectly into the weekly routine without confusion or overlap.",
    keyPoints: [
      'Flexible subject distribution across the week with drag-and-drop visual timetable interface',
      'Intelligent validation rules prevent conflicts (teacher double-booking, room conflicts)',
      'Real-time updates available to teachers, students, and parents with instant synchronization across all records'
    ],
    detailedDescription: `From the dashboard, the Admin can select any grade or class group and begin assigning subjects and teachers according to the school's available days and hours. The Admin chooses which days each teacher works, the number of study hours per subject, and the method of distribution across the week.

For example, if a subject like Mathematics has 5 weekly hours, the Admin can divide it as 2 hours on Monday, 2 on Wednesday, and 1 on Friday — or any pattern that suits the class schedule. This flexibility allows each school to follow its own teaching rhythm while keeping a balanced load for students and teachers.

The system also supports multiple class groups, meaning the Admin can assign different subjects or teachers to different sections of the same grade. Each timetable adapts automatically to fit those configurations.

JeridSchool's interface uses a clear visual timetable view, where the Admin can drag, drop, or modify subjects and hours easily. Any changes — such as replacing a teacher or switching a classroom — are updated instantly and reflected across all related records.

Importantly, the system includes intelligent validation rules that prevent human errors. For instance, it will not allow a teacher to teach two classes at the same time, or assign a room to two groups simultaneously. It also checks that subject hours are correctly distributed according to the school's academic plan.

The Admin can view the timetable in a daily or weekly layout to verify balance and identify free periods. Adjustments can be made at any time, and the interface ensures every update is accurate and consistent.

Once finalized, the timetable becomes immediately available to teachers, students, and parents, ensuring everyone knows exactly when and where lessons occur.

This system eliminates scheduling chaos, paperwork, and misunderstandings. It gives schools total control over teaching hours, resources, and time — creating a smooth, fair, and transparent academic routine for everyone involved.`
  },
  {
    id: 3,
    icon: <ClipboardCheck className="w-8 h-8" />,
    title: 'Attendance Management System',
    summary: 'The Attendance Management System in JeridSchool provides the Admin with full control and visibility over student and teacher attendance across all grades, subjects, and classes. It ensures that every session is properly tracked, making the learning process more accountable and transparent.',
    keyPoints: [
      'Real-time attendance tracking with automatic synchronization from teacher dashboards eliminating paper sheets',
      'Filter attendance by class, subject, teacher, or date with analytics including monthly absence rates and punctuality trends',
      'Automated parent notifications for absences and ability to assign substitute teachers ensuring lessons continue without interruption'
    ],
    detailedDescription: `Each day, teachers mark attendance directly from their own dashboard, selecting which students are present, absent, or late. This information is automatically synchronized with the Admin's dashboard in real time, eliminating the need for paper sheets or manual collection.

From the Admin interface, attendance can be filtered by class, subject, teacher, or specific date, giving a clear and structured view of daily and historical data. The Admin can instantly identify which students are consistently late, frequently absent, or have justified excuses.

If an error occurs or a student's absence is justified (for example, due to illness or school permission), the Admin can edit or approve the record with just a few clicks. This flexibility keeps the system accurate and up to date.

The Admin also has the ability to assign substitute teachers when a teacher is planned to be AFK (away from class) — ensuring that lessons continue without interruption. The system records both the original and replacement teachers for accountability and scheduling purposes.

In addition, JeridSchool generates attendance analytics and statistics, such as monthly absence rates, punctuality trends, and student participation summaries. These reports help identify behavior patterns and assist the school in providing early support to at-risk students.

Parents automatically receive notifications when their child is marked absent or late, strengthening communication between home and school and encouraging responsibility among students.

The attendance data also connects seamlessly with other systems — for example, timetable and performance analytics — to ensure consistency across the entire platform.

With JeridSchool, attendance tracking becomes a smart, paperless, and real-time process, freeing Admins from manual work and ensuring complete transparency. It allows schools to maintain discipline, improve accountability, and react quickly to any attendance-related issues.

Ultimately, the Attendance Management System transforms daily record-keeping into valuable insights, helping schools create a more organized, reliable, and efficient academic environment.`
  },
  {
    id: 4,
    icon: <Shield className="w-8 h-8" />,
    title: 'SuperAdmin – Manage Admins',
    summary: 'The SuperAdmin is the top-level user in JeridSchool with full authority over the entire platform. One of their key responsibilities is to manage Admin accounts for multiple schools. They can create, edit, or deactivate Admins, assign roles, and control their access to specific school data.',
    keyPoints: [
      'Create, edit, or deactivate Admin accounts for multiple schools with centralized dashboard monitoring',
      'Reset passwords, update permissions, and provide support ensuring efficient and secure Admin operations',
      'Maintain standardized operational structure across all schools enforcing consistent policies and data security'
    ],
    detailedDescription: `The SuperAdmin is the top-level user in JeridSchool with full authority over the entire platform. One of their key responsibilities is to manage Admin accounts for multiple schools. They can create, edit, or deactivate Admins, assign roles, and control their access to specific school data.

Through a centralized dashboard, the SuperAdmin monitors Admin activity, ensuring that each Admin performs their duties efficiently and securely. They can reset passwords, update permissions, and provide support when needed.

This oversight ensures consistency across all schools using JeridSchool, allowing the SuperAdmin to maintain a standardized operational structure and enforce school policies effectively.

By managing Admins, the SuperAdmin guarantees that schools operate smoothly, data remains secure, and each Admin can focus on running their school without technical interruptions.`
  },
  {
    id: 5,
    icon: <Globe className="w-8 h-8" />,
    title: 'SuperAdmin – Manage School Websites',
    summary: "Another key responsibility of the SuperAdmin is to manage the school websites for each institution using JeridSchool. They can create and customize websites for different schools, ensuring that each site reflects the school's identity, branding, and essential information.",
    keyPoints: [
      'Customize school websites with logo, colors, navigation menus, and homepage layout with multilingual support (Arabic, French, English)',
      'Manage content sections (About Us, Admissions, News, Events, Contact) and enable/disable features like online registration',
      'Monitor website performance and analytics while maintaining security and quality across all school websites'
    ],
    detailedDescription: `Another key responsibility of the SuperAdmin is to manage the school websites for each institution using JeridSchool. They can create and customize websites for different schools, ensuring that each site reflects the school's identity, branding, and essential information.

The SuperAdmin controls website settings such as the school's logo, colors, navigation menus, and homepage layout. They can also manage content sections like About Us, Admissions, News, Events, and Contact information.

Through this system, SuperAdmins can enable or disable features such as online registration forms, news feeds, and event calendars. They can also manage multiple languages (Arabic, French, English) to accommodate students, parents, and staff in Tunisia.

They can assign Admins or content managers to update website content regularly while retaining ultimate control over what is published. This ensures that all public information is accurate, up to date, and aligned with the school's standards.

Additionally, the SuperAdmin can monitor website performance, view analytics such as visitor counts and popular pages, and make improvements to enhance user experience.

This feature allows schools to maintain a professional online presence without needing separate IT teams, while the SuperAdmin ensures security, consistency, and quality across all school websites.

By managing both Admin accounts and school websites, the SuperAdmin guarantees that JeridSchool's platform is efficient, organized, and visually appealing, giving schools a competitive advantage in education management and communication.`
  },
  {
    id: 6,
    icon: <CreditCard className="w-8 h-8" />,
    title: 'Student Card Configuration',
    summary: 'Configure student cards with essential information including name, contact details, academic year, and school logo. This ensures the identity of the school is properly displayed across the system, making student identification and management seamless.',
    keyPoints: [
      'Customize student cards with name, contact details, academic year, and school logo for proper identification',
      'Ensure school identity is consistently displayed across all system modules and interfaces',
      'Streamline student data management with centralized card configuration and automated updates'
    ],
    detailedDescription: `Configure student cards with essential information including name, contact details, academic year, and school logo. This ensures the identity of the school is properly displayed across the system, making student identification and management seamless.

After setup, the student cards serve as a comprehensive identification system that displays consistently across all modules and interfaces. The school's branding and identity are properly represented, creating a professional and cohesive experience throughout the platform.

This centralized card configuration streamlines student data management, allowing for easy updates and ensuring that all information remains synchronized across the entire system.`
  }
]

function AdminFeatureDetailPage() {
  const { id } = useParams({ from: Route.fullPath })
  const featureId = parseInt(id || '0', 10)
  const feature = features.find(f => f.id === featureId)
  const mainColor = '#525FE1'

  // Debug logging
  console.log('Feature Detail Page - ID from params:', id)
  console.log('Feature ID parsed:', featureId)
  console.log('Feature found:', feature)
  console.log('Available features:', features.map(f => f.id))

  if (!id || isNaN(featureId) || !feature) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Feature Not Found</h1>
          <p className="text-gray-600 mb-6">The feature you're looking for doesn't exist.</p>
          <Link
            to="/feature/admin"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Features
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-12 md:py-16">
        {/* Header */}
        <div className="mb-12">
          <Link
            to="/feature/admin"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-8 transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Features
          </Link>
          
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-sm" style={{ backgroundColor: mainColor }}>
              <div className="text-white">
                {feature.icon}
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {feature.title}
            </h1>
            <p className="text-lg text-gray-600">
              {feature.summary}
            </p>
          </div>
        </div>

        {/* Detailed Content */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl border border-gray-200 p-8 md:p-10 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ color: mainColor }}>
              Detailed Overview
            </h2>
            <div className="prose prose-base max-w-none">
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {feature.detailedDescription}
              </p>
            </div>
          </div>

          {/* Key Points */}
          <div className="bg-white rounded-xl border border-gray-200 p-8 md:p-10">
            <h3 className="text-xl font-semibold text-gray-900 mb-6" style={{ color: mainColor }}>
              Key Points
            </h3>
            <ul className="space-y-3">
              {feature.keyPoints.map((point, index) => (
                <li key={index} className="flex items-start text-base text-gray-700">
                  <span 
                    className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0"
                    style={{ backgroundColor: mainColor }}
                  ></span>
                  <span className="leading-relaxed">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}